import { cloneDeep, merge, mergeWith, omit } from 'lodash-es';
import { blockType, generateChildNode } from '@/libs/flow/const';
import { normalizeAnnotations } from '@/views/flow/FlowGrap/flowAnnotations';
import {
  initExceptionHandling,
  initOutputFormat,
  normalizeFlowEdgesForCanvas
} from '@/views/flow/helper/exceptionHandling';

/**
 */
export function normalizeTaskFlowNodesFromApi(nodes) {
  if (!Array.isArray(nodes)) return [];
  const normalizedNodes = nodes
    .filter(n => n && typeof n === 'object' && n.id != null && n.type != null)
    .map(n => {
      const isRootCanvasNode =
        n.type === blockType.block || n.type === blockType.start;
      const dataFromApi =
        n.data && typeof n.data === 'object' ? cloneDeep(n.data) : {};
      if (dataFromApi.showToolBar === undefined) {
        dataFromApi.showToolBar = false;
      }
      const node = {
        id: n.id,
        type: n.type,
        position: {
          x: Number(n.position?.x) || 0,
          y: Number(n.position?.y) || 0
        },
        data: dataFromApi,
        hidden: isRootCanvasNode ? n.hidden === true : true
      };
      if (n.parentNode != null) {
        node.parentNode = n.parentNode;
      }
      if (n.style != null && typeof n.style === 'object') {
        node.style = cloneDeep(n.style);
      }
      if (n.blockId != null) {
        node.blockId = n.blockId;
      }
      if (n.originalBlockId != null) {
        node.originalBlockId = n.originalBlockId;
      }
      if (!isRootCanvasNode) {
        let defaults = null;
        try {
          defaults = generateChildNode({
            type: n.type,
            blockId: n.blockId || '',
            childId: n.id,
            config: n.config
          });
        } catch {
          defaults = null;
        }
        if (defaults) {
          node.data = merge({}, defaults.data || {}, dataFromApi);
          node.config = mergeWith(
            {},
            cloneDeep(defaults.config || {}),
            n.config && typeof n.config === 'object' ? cloneDeep(n.config) : {},
            (objValue, srcValue) => {
              if (Array.isArray(srcValue)) {
                return cloneDeep(srcValue);
              }
              return undefined;
            }
          );
          if (defaults.blockId != null && node.blockId == null) {
            node.blockId = defaults.blockId;
          }
        } else if (n.config && typeof n.config === 'object') {
          node.config = cloneDeep(n.config);
        }
        if (node.config && typeof node.config === 'object') {
          initExceptionHandling(node.config);
          if (['llMReply', 'llmVariableAssignment'].includes(n.type)) {
            initOutputFormat(node.config);
          }
        }
      }
      return node;
    });

  const nodeMap = new Map(normalizedNodes.map(node => [node.id, node]));
  const originalBlockIdByChildId = new Map();
  normalizedNodes.forEach(node => {
    const childIds = node.data?.include_node_ids;
    if (
      node.type === blockType.block &&
      node.hidden === true &&
      Array.isArray(childIds) &&
      childIds.length === 1
    ) {
      const childId = childIds[0];
      originalBlockIdByChildId.set(
        childId,
        originalBlockIdByChildId.has(childId) ? null : node.id
      );
    }
  });
  normalizedNodes.forEach(node => {
    if (node.type === blockType.block || node.originalBlockId != null) {
      return;
    }
    const originalBlockId = originalBlockIdByChildId.get(node.id);
    const currentBlock = nodeMap.get(node.blockId);
    if (
      !originalBlockId ||
      originalBlockId === node.blockId ||
      currentBlock?.type !== blockType.block ||
      !currentBlock.data?.include_node_ids?.includes(node.id)
    ) {
      return;
    }
    node.originalBlockId = originalBlockId;
  });
  normalizedNodes.forEach(node => {
    if (node.type !== blockType.block || node.data?.is_group === true) {
      return;
    }
    const childIds = node.data?.include_node_ids;
    if (!Array.isArray(childIds) || childIds.length === 0) {
      return;
    }
    const isPersistedGroup = childIds.every(childId => {
      const child = nodeMap.get(childId);
      const originalBlock = nodeMap.get(child?.originalBlockId);
      return (
        child?.blockId === node.id &&
        originalBlock?.type === blockType.block &&
        originalBlock.hidden === true &&
        originalBlock.data?.include_node_ids?.length === 1 &&
        originalBlock.data.include_node_ids[0] === child.id
      );
    });
    if (isPersistedGroup) {
      node.data.is_group = true;
    }
  });

  return normalizedNodes;
}

export function mergeTaskFlowApiResponseToSchema(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      nodes: [],
      edges: [],
      annotations: [],
      flow_uuid: '',
      trigger_list: [],
      is_trigger: false,
      config: {}
    };
  }
  const wf =
    raw.workflow &&
    typeof raw.workflow === 'object' &&
    !Array.isArray(raw.workflow)
      ? raw.workflow
      : {};
  const wfCanvas = omit(wf, ['trigger_list', 'is_trigger']);
  const merged = cloneDeep({
    ...raw,
    ...wfCanvas
  });
  delete merged.workflow;
  merged.trigger_list = Array.isArray(raw.trigger_list)
    ? raw.trigger_list
    : Array.isArray(wf.trigger_list)
    ? wf.trigger_list
    : [];
  if (raw.is_trigger !== undefined && raw.is_trigger !== null) {
    merged.is_trigger = !!raw.is_trigger;
  } else if (wf.is_trigger !== undefined && wf.is_trigger !== null) {
    merged.is_trigger = !!wf.is_trigger;
  } else {
    merged.is_trigger = merged.trigger_list.length > 0;
  }
  const nodeSource = Array.isArray(wf.nodes)
    ? wf.nodes
    : Array.isArray(merged.nodes)
    ? merged.nodes
    : [];
  merged.nodes = normalizeTaskFlowNodesFromApi(nodeSource);
  merged.edges = normalizeFlowEdgesForCanvas(
    Array.isArray(merged.edges) ? merged.edges : [],
    merged.nodes
  );
  merged.annotations = normalizeAnnotations(merged.annotations);
  merged.update_time = raw.draft_saved_at || raw.update_time || '';
  merged.workflow_nodes = Array.isArray(merged.workflow_nodes)
    ? merged.workflow_nodes
    : [];
  if (!merged.config || typeof merged.config !== 'object') {
    merged.config = {};
  }
  return merged;
}
